import { useSelector } from 'react-redux';
import { useTheme } from '@/App';
import { getAccentColor } from '@/themes/antd-theme';

// Converts a "#rrggbb" color into an rgba() string at the given alpha, so
// tag/box backgrounds can be tinted from the same accent color used for
// text/icons/borders.
export const hexToRgba = (hex, alpha) => {
  const clean = (hex || '').replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const bigint = parseInt(full, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Blends a "#rrggbb" color toward white by the given percent (0-1), so a
// brand color that's too dark to read as text on a dark card background can
// be lightened while keeping its hue.
const lightenColor = (hex, percent) => {
  const clean = (hex || '').replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const bigint = parseInt(full, 16);
  const r = Math.round(((bigint >> 16) & 255) + (255 - ((bigint >> 16) & 255)) * percent);
  const g = Math.round(((bigint >> 8) & 255) + (255 - ((bigint >> 8) & 255)) * percent);
  const b = Math.round((bigint & 255) + (255 - (bigint & 255)) * percent);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Shared accent-color derivation for sub-operation card views (Ingredients,
 * Monitoring, Checklist, ActionList, etc.) so their tags/badges/icon
 * backgrounds all track the company's configured theme color instead of
 * hardcoded Tailwind hues.
 */
const useAccentTheme = () => {
  const { isDark } = useTheme();
  const { theme_color } = useSelector((state) => state.company_profile);
  // Always resolve the theme's true (light-mode) brand color first — the
  // dark-mode branch of getAccentColor() darkens it for solid button fills,
  // which is unreadable used as foreground text on a dark card background.
  const baseAccent = getAccentColor(theme_color, false);
  const accentColor = isDark ? lightenColor(baseAccent, 0.55) : baseAccent;
  const accentBgSoft = hexToRgba(baseAccent, isDark ? 0.3 : 0.1);
  const accentBgSofter = hexToRgba(baseAccent, isDark ? 0.2 : 0.06);
  const accentTagStyle = { backgroundColor: accentBgSoft, color: accentColor, border: 'none' };

  return { isDark, accentColor, accentBgSoft, accentBgSofter, accentTagStyle, hexToRgba };
};

export default useAccentTheme;
